import { type FC, useEffect } from 'react'
import { useClearRefinements, useStats } from 'react-instantsearch'
import { FormattedMessage } from 'react-intl'
import { useMediaQuery } from 'usehooks-ts'

import {
  Button,
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTitle,
} from 'base-ui'
import screens from 'tailwind-preset/screens.cjs'

import { ProductSearchRefinements } from '../ProductSearchRefinements'
import { ProductSearchRefinementSortingChipButtonList } from '../ProductSearchRefinementSortingChipButtonList'
import { ProductSearchRefinementStateProvider } from '../ProductSearchRefinementStateContext'

import styles from './ProductSearchRefinementsDialog.module.css'

export const ProductSearchRefinementsDialog: FC<
  Pick<DialogProps, 'onClose' | 'open'>
> = ({ onClose, open }) => {
  const matches = useMediaQuery(`(min-width: ${screens.desktop})`, {
    defaultValue: false,
    initializeWithValue: false,
  })

  const { nbHits } = useStats()
  const { canRefine, refine } = useClearRefinements()

  useEffect(() => {
    /**
     * Close the dialog when the screen size changes to desktop and the dialog is open.
     */
    if (open && matches) {
      onClose?.({}, 'backdropClick')
    }
  }, [matches, onClose, open])

  return (
    <Dialog
      onClose={onClose}
      open={open}
      slotProps={{
        dialog: {
          className: styles['product-search-refinements-dialog'],
        },
      }}
    >
      <DialogTitle onClose={onClose}>
        <FormattedMessage id="general.label.selectFilter" />
      </DialogTitle>
      <DialogContent className="flex flex-1 flex-col gap-6 bg-light-primary-low p-0">
        <ProductSearchRefinementStateProvider isMobile>
          <ProductSearchRefinementSortingChipButtonList />
          <div className="flex flex-col gap-6 p-4">
            <ProductSearchRefinements />
          </div>
        </ProductSearchRefinementStateProvider>
        <div className="sticky bottom-0 flex flex-col gap-4 p-4">
          <Button
            data-qa-id="search-page-refinement-list-dialog-close"
            onClick={() => {
              onClose?.({}, 'backdropClick')
            }}
          >
            {canRefine ? (
              <FormattedMessage
                id="general.label.showResults"
                values={{
                  count: nbHits,
                }}
              />
            ) : (
              <FormattedMessage id="general.link.label.close" />
            )}
          </Button>

          <Button
            data-qa-id="search-page-refinement-list-dialog-clear-filters"
            disabled={!canRefine}
            onClick={refine}
          >
            <FormattedMessage id="general.label.clearFilters" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
